/**
 * Terms of Service Page
 * Comprehensive terms for CueSesh podcast app
 * Compliant with Apple App Store/TestFlight requirements
 *
 * 2026-07-13: interim consumer-protection carve-outs for the international
 * wave-1 open (AU/NZ/UK). Self-drafted DEFENSIVE language (savings clauses +
 * carve-outs only — narrows our claimed rights, adds none) pending counsel
 * ratification; see cuesesh-api/docs/counsel/terms-uct-interim-2026-07.md.
 * Counsel owns any restructure (region-split terms, arbitration scope).
 */

import Header from '../components/Header';
import { MetaTags } from '../components/SEO';
import './Legal.css';

export default function Terms() {
  return (
    <>
      <MetaTags
        title="Terms of Service"
        description="CueSesh Terms of Service. Read our terms for app usage, user responsibilities, and service terms. Simple and transparent."
        canonicalUrl="https://cuesesh.com/terms"
        noindex={false}
      />

      <div className="legal-page">
        <Header />

        <div className="legal-page-with-header">
          <div className="legal-container">
            <h1 className="legal-title">Terms of Service</h1>
            <p className="legal-subtitle">Last updated: July 14, 2026</p>

            <div className="legal-content">
              <p>
                Welcome to CueSesh. The Service is provided by <strong>CueSesh LLC</strong>
                (&ldquo;CueSesh&rdquo;, &ldquo;we&rdquo;). These Terms of Service ("Terms") govern
                your use of the CueSesh mobile application and related services (collectively, the
                "Service"). By creating an account or using CueSesh, you agree to be bound by these
                Terms.
              </p>

              <div className="legal-highlight">
                <strong>Plain Language Promise:</strong> We've written these terms to be understandable.
                Where legal precision is required, we've included it, but we believe you deserve to
                know what you're agreeing to.
              </div>

              <h2>1. Service Description</h2>
              <p>
                CueSesh is a podcast listening application that allows you to discover, stream, and
                organize podcast content. The Service includes:
              </p>
              <ul>
                <li>Podcast discovery and streaming from public RSS feeds</li>
                <li>Personalized recommendations based on your listening habits</li>
                <li>Playback progress syncing across devices</li>
                <li>Queue management and playlist organization</li>
                <li>Listening history and statistics</li>
              </ul>

              <h2>2. License Grant</h2>
              <p>
                Subject to your compliance with these Terms, CueSesh grants you a limited,
                non-exclusive, non-transferable, revocable license to download, install, and use
                the application on devices you own or control, solely for your personal,
                non-commercial purposes.
              </p>
              <p>You may NOT:</p>
              <ul>
                <li>Copy, modify, or distribute the application</li>
                <li>Reverse engineer, decompile, or disassemble the application</li>
                <li>Rent, lease, lend, sell, or sublicense the application</li>
                <li>Use the application for any commercial purpose without our written consent</li>
                <li>Remove or alter any proprietary notices or labels</li>
              </ul>

              <h2>3. Account Registration</h2>
              <p>To use CueSesh, you must create an account. You agree to:</p>
              <ul>
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Promptly notify us of any unauthorized access to your account</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>
              <p>
                You must be at least 13 years old to create an account. If you are under 18, you
                represent that your parent or legal guardian has reviewed and agreed to these Terms.
              </p>

              <h2>4. Third-Party Podcast Content</h2>
              <div className="legal-highlight">
                <strong>Important:</strong> CueSesh does not create, own, or control podcast content.
                All podcasts available through our Service are created by independent third-party
                creators and publishers.
              </div>
              <p>
                CueSesh aggregates podcast content from publicly available RSS feeds and podcast
                directories. We stream audio and video directly from the original hosting sources —
                we do not host podcast media files on our servers.
              </p>
              <p>You understand and agree that:</p>
              <ul>
                <li>
                  <strong>Content Responsibility:</strong> Podcast creators and publishers are solely
                  responsible for their content. CueSesh does not endorse, verify, or guarantee the
                  accuracy, completeness, or quality of any podcast content.
                </li>
                <li>
                  <strong>Views & Opinions:</strong> The views, opinions, and statements expressed in
                  podcasts are those of the creators and do not represent the views of CueSesh.
                </li>
                <li>
                  <strong>Content Availability:</strong> Podcasts may be removed, modified, or become
                  unavailable at any time at the discretion of the content creators or their hosting
                  providers.
                </li>
                <li>
                  <strong>No Liability:</strong> CueSesh is not liable for any claims arising from
                  third-party podcast content, including but not limited to defamation, copyright
                  infringement, or harmful content.
                </li>
              </ul>

              <h2>5. DMCA & Copyright Policy</h2>
              <p>
                CueSesh respects intellectual property rights and complies with the Digital Millennium
                Copyright Act (DMCA). If you believe content accessible through CueSesh infringes your
                copyright, please contact us at{' '}
                <a href="mailto:support@cuesesh.com">support@cuesesh.com</a> with:
              </p>
              <ul>
                <li>Identification of the copyrighted work</li>
                <li>Identification of the infringing material and its location</li>
                <li>Your contact information</li>
                <li>A statement of good faith belief that the use is unauthorized</li>
                <li>A statement, under penalty of perjury, that your notice is accurate</li>
                <li>Your physical or electronic signature</li>
              </ul>
              <p>
                Note: Since we aggregate content from third-party sources, copyright complaints are
                typically best directed to the podcast hosting provider. We will assist in identifying
                the appropriate party when possible.
              </p>

              <h2>6. Acceptable Use</h2>
              <p>When using CueSesh, you agree NOT to:</p>
              <ul>
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon the rights of others</li>
                <li>Use automated tools, bots, or scripts to access the Service</li>
                <li>Attempt to bypass security measures or access restricted areas</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Harvest or collect user information without consent</li>
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Redistribute, record, or rebroadcast podcast content</li>
              </ul>

              <h2>7. User Data & Ratings</h2>
              <p>
                CueSesh may allow you to rate podcasts, save favorites, and create playlists. You
                retain ownership of any personal data you provide. By using these features, you grant
                CueSesh a non-exclusive license to use this data to provide and improve the Service,
                including generating aggregate statistics and recommendations.
              </p>
              <p>
                We may display aggregate, anonymized data (such as podcast popularity rankings) but
                will not publicly associate your personal ratings or listening history with your
                identity without your consent.
              </p>

              <h2>8. Account Suspension & Termination</h2>
              <p>
                <strong>By You:</strong> You may delete your account at any time, directly in the
                app — <strong>Settings &rarr; Delete account</strong> — no email required. (No longer
                have the app? Contact <a href="mailto:support@cuesesh.com">support@cuesesh.com</a>.)
                Upon deletion, we will remove your personal data in accordance with our Privacy Policy.
              </p>
              <p>
                <strong>By Us:</strong> We reserve the right to suspend or terminate your account
                if you:
              </p>
              <ul>
                <li>Violate these Terms</li>
                <li>Engage in fraudulent or illegal activity</li>
                <li>Abuse the Service or other users</li>
                <li>Fail to pay any applicable fees (if/when introduced)</li>
              </ul>
              <p>
                Where practicable, we will give you reasonable notice and an opportunity to remedy
                the issue first. We may act immediately where the violation is serious or unlawful,
                or creates risk to the Service or other users.
              </p>
              <p>
                Upon termination, your license to use the Service ends immediately. Sections of these
                Terms that by their nature should survive termination will survive (including
                limitations of liability, indemnification, and dispute resolution).
              </p>

              <h2>9. Beta Testing & TestFlight</h2>
              <p>
                If you are accessing CueSesh through Apple TestFlight or another beta program, you
                understand and agree that:
              </p>
              <ul>
                <li>The application is pre-release software and may contain bugs or errors</li>
                <li>Features may be incomplete, change, or be removed before public release</li>
                <li>Data collected during beta may not transfer to the final release</li>
                <li>You agree to provide feedback and report bugs when possible</li>
                <li>
                  You will not publicly share screenshots, features, or details about unreleased
                  functionality without our permission
                </li>
                <li>Your beta access may be revoked at any time for any reason</li>
              </ul>
              <div className="legal-highlight">
                <strong>Beta Disclaimer:</strong> Beta versions are provided "AS IS" with all faults.
                We make no guarantees about stability, data preservation, or feature availability
                during the beta period.
              </div>

              <h2>10. Disclaimer of Warranties</h2>
              <p>
                <strong>
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                  EITHER EXPRESS OR IMPLIED.
                </strong>
              </p>
              <p>To the fullest extent permitted by law, CueSesh disclaims all warranties, including:</p>
              <ul>
                <li>Implied warranties of merchantability, fitness for a particular purpose, and non-infringement</li>
                <li>Warranties that the Service will be uninterrupted, error-free, or secure</li>
                <li>Warranties regarding the accuracy or reliability of any content</li>
                <li>Warranties that defects will be corrected</li>
              </ul>
              <p>
                Some jurisdictions do not allow the exclusion of certain warranties, so some of the
                above exclusions may not apply to you.
              </p>
              <p>
                If you are a consumer in Australia: our services come with guarantees that cannot be
                excluded under the Australian Consumer Law, and nothing in these Terms excludes,
                restricts, or modifies those guarantees. Consumers in the United Kingdom, New Zealand,
                and elsewhere may have equivalent statutory rights that these Terms do not affect.
              </p>

              <h2>11. Limitation of Liability</h2>
              <p>
                <strong>
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, CUESESH AND ITS OFFICERS, DIRECTORS,
                  EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                  CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
                </strong>
              </p>
              <ul>
                <li>Loss of profits, data, or goodwill</li>
                <li>Service interruption or computer damage</li>
                <li>Cost of substitute services</li>
                <li>Any damages arising from third-party content</li>
                <li>Any matter beyond our reasonable control</li>
              </ul>
              <p>
                <strong>
                  IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE GREATER OF: (A) THE AMOUNT YOU
                  PAID TO CUESESH IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED
                  U.S. DOLLARS ($100).
                </strong>
              </p>
              <p>
                These limitations apply regardless of the theory of liability (contract, tort, or
                otherwise) and even if we have been advised of the possibility of such damages.
              </p>
              <p>
                Nothing in this section limits or excludes our liability for death or personal injury
                caused by our negligence, for fraud or fraudulent misrepresentation, or for any other
                liability that cannot be limited or excluded by applicable law.
              </p>

              <h2>12. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless CueSesh and its officers, directors,
                employees, contractors, and agents from and against any claims, damages, losses,
                liabilities, costs, and expenses (including reasonable attorneys' fees) to the extent
                arising out of:
              </p>
              <ul>
                <li>Your violation of these Terms</li>
                <li>Your violation of any rights of another party</li>
                <li>Your violation of any applicable laws or regulations</li>
                <li>Your misuse of the Service</li>
              </ul>
              <p>
                This indemnity does not apply to the extent a claim arises from our own negligence,
                fraud, or breach of these Terms, and it does not limit any right you have under
                applicable law that cannot be waived by agreement.
              </p>

              <h2>13. Dispute Resolution & Arbitration</h2>
              <div className="legal-highlight">
                <strong>Please Read Carefully:</strong> This section affects your legal rights,
                including your right to file a lawsuit in court.
              </div>
              <p>
                <strong>Informal Resolution First:</strong> Before filing any claim, you agree to
                contact us at <a href="mailto:support@cuesesh.com">support@cuesesh.com</a> and attempt
                to resolve the dispute informally for at least 30 days.
              </p>
              <p>
                <strong>Binding Arbitration:</strong> If we cannot resolve the dispute informally, you
                and CueSesh agree to resolve any claims through final and binding arbitration, except
                as set forth below. The arbitration will be conducted by JAMS under its Streamlined
                Arbitration Rules. The arbitration will be conducted in English, and judgment on the
                award may be entered in any court with jurisdiction.
              </p>
              <p>
                <strong>Class Action Waiver:</strong> YOU AND CUESESH AGREE THAT EACH MAY BRING CLAIMS
                AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR
                CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
              </p>
              <p>
                <strong>Exceptions:</strong> Either party may bring claims in small claims court if
                eligible. Either party may seek injunctive relief in any court of competent
                jurisdiction for infringement of intellectual property rights.
              </p>
              <p>
                <strong>Opt-Out:</strong> You may opt out of this arbitration agreement by sending
                written notice to <a href="mailto:support@cuesesh.com">support@cuesesh.com</a> within
                30 days of first accepting these Terms. Your notice must include your name, address,
                and a clear statement that you wish to opt out.
              </p>
              <p>
                <strong>Consumers Outside the United States:</strong> If you are a consumer and the
                law of the country where you live grants you rights that cannot be waived by agreement
                — including the right to bring proceedings in your local courts under your local law —
                those rights prevail over this section. Nothing in this section requires consumers in
                the United Kingdom, European Union, Australia, or New Zealand to arbitrate a dispute,
                or waives their access to local courts or consumer-protection remedies.
              </p>

              <h2>14. Apple-Specific Terms</h2>
              <p>
                If you download CueSesh from the Apple App Store, the following additional terms apply:
              </p>
              <ul>
                <li>
                  These Terms are between you and CueSesh only, not Apple. CueSesh, not Apple, is
                  solely responsible for the Service and its content.
                </li>
                <li>
                  Your license to use the app is limited to a non-transferable license on any Apple
                  device that you own or control, as permitted by the App Store Terms of Service.
                </li>
                <li>
                  Apple has no obligation to provide maintenance or support services for the app.
                </li>
                <li>
                  Apple is not responsible for any product warranties. If the app fails to conform
                  to any applicable warranty, you may notify Apple for a refund of the purchase
                  price (if any). Apple has no other warranty obligation.
                </li>
                <li>
                  Apple is not responsible for addressing any claims relating to the app or your
                  use of it, including product liability claims, claims that the app fails to
                  conform to legal requirements, or claims under consumer protection laws.
                </li>
                <li>
                  Apple is not responsible for investigating, defending, settling, or discharging
                  any claim that the app infringes a third party's intellectual property rights.
                </li>
                <li>
                  Apple and its subsidiaries are third-party beneficiaries of these Terms. Upon
                  your acceptance, Apple will have the right to enforce these Terms against you
                  as a third-party beneficiary.
                </li>
                <li>
                  You represent that you are not located in a country subject to a U.S. Government
                  embargo and are not on any U.S. Government list of prohibited or restricted parties.
                </li>
              </ul>

              <h2>15. Content Moderation &amp; Illegal-Content Notices (EU Digital Services Act)</h2>
              <p>
                CueSesh is a podcast index: we catalog shows and episodes from publicly available
                RSS feeds and stream audio and video directly from the publishers&rsquo; own hosting.
                We do not host podcast media ourselves, and the restrictions on how you may use the
                Service are set out in Section 6 (Acceptable Use) above.
              </p>
              <p>
                <strong>Our moderation policy, plainly:</strong> if content in our catalog is
                illegal, or a feed violates these Terms, we remove or restrict access to the
                affected catalog entries (show or episode listings) for our users. Because
                content lives on publishers&rsquo; servers, removal from CueSesh removes it from
                our index and playback surfaces, not from the wider internet.
              </p>
              <p>
                <strong>Reporting illegal content (notice &amp; action):</strong> anyone —
                whether or not they use CueSesh — can report allegedly illegal content in our
                catalog by emailing{' '}
                <a href="mailto:legal@cuesesh.com">legal@cuesesh.com</a>. Please include:
              </p>
              <ul>
                <li>Why you believe the content is illegal</li>
                <li>
                  Exactly where it is — the show and episode name, and a link or screenshot if
                  you have one
                </li>
                <li>
                  Your name and email address (not required for material involving child sexual
                  abuse)
                </li>
                <li>A statement that your report is made in good faith and is accurate</li>
              </ul>
              <p>
                We will confirm receipt of your notice electronically, review it promptly and
                without discrimination, and tell you what we decided and why. If we remove or
                restrict content, we will also give the affected party a statement of our
                reasons where we have their contact details.
              </p>
              <p>
                <strong>This is not account deletion.</strong> Reporting illegal content is for
                anyone flagging unlawful material in the catalog. Deleting your own account and
                data is a separate, self-service action — see Settings &rarr; Delete account in
                the app and our <a href="/privacy">Privacy Policy</a>.
              </p>
              <p>
                <strong>Point of contact (DSA Articles 11 &amp; 12):</strong> our single
                electronic point of contact for member-state authorities, the European
                Commission, the European Board for Digital Services, and recipients of the
                Service is <a href="mailto:legal@cuesesh.com">legal@cuesesh.com</a>.
                Communications are accepted in English. Postal contact: CueSesh LLC, 7216
                Bergenline Ave #1054, North Bergen, NJ 07047, United States. Phone: (917)
                830-5632.
              </p>
              {/*
                DSA contact details differ from the GDPR ones — digitalrequest@ (not
                datarequest@), and a single Cork postal address rather than the 30
                EU/EEA/UK locations. Source: CUES02 Representative contact summary (DSA).
                DataRep also advise keeping this declaration OUT of the privacy notice,
                since the DSA is not a data-protection regime — hence Terms, not Privacy.
              */}
              <p>
                <strong>EU legal representative (DSA Article 13):</strong> CueSesh LLC has appointed{' '}
                <strong>Data Protection Representative Limited (trading as DataRep)</strong> as its
                legal representative in the European Union for the purposes of the Digital Services
                Act (Regulation (EU) 2022/2065). Member-state authorities, the European Commission,
                and the European Board for Digital Services may address DataRep in addition to us.
                Individuals may also contact DataRep to raise concerns or report illegal content.
              </p>
              <p>
                DataRep can be reached by email at{' '}
                <a href="mailto:digitalrequest@datarep.com">digitalrequest@datarep.com</a> — please
                quote &ldquo;CueSesh LLC&rdquo; in the subject line — via their online form at{' '}
                <a href="https://www.datarep.com/data-request" target="_blank" rel="noopener noreferrer">
                  www.datarep.com/data-request
                </a>
                , by telephone on +353 (1) 919 8899, or by post at DataRep, The Cube, Monahan Road,
                Cork, T12 H1XY, Republic of Ireland. Postal correspondence must be marked for the
                attention of &ldquo;DataRep&rdquo; — not CueSesh LLC — or it may not reach them. For
                general questions about the CueSesh app, please contact us directly at{' '}
                <a href="mailto:support@cuesesh.com">support@cuesesh.com</a> rather than our
                representative.
              </p>

              <h2>16. Privacy</h2>
              <p>
                Your privacy is important to us. Our{' '}
                <a href="/privacy">Privacy Policy</a> explains how we collect, use, and protect your
                personal information. By using CueSesh, you agree to our Privacy Policy.
              </p>

              <h2>17. Changes to Service & Terms</h2>
              <p>
                <strong>Service Changes:</strong> We may modify, suspend, or discontinue any part of
                the Service. Where a change materially reduces the Service, we will give reasonable
                advance notice where practicable. To the extent permitted by law, we are not liable
                to you or any third party for any modification, suspension, or discontinuation —
                though nothing in this section affects statutory rights that cannot be excluded.
              </p>
              <p>
                <strong>Terms Changes:</strong> We may update these Terms from time to time. If we
                make material changes, we will notify you in the app and update the date at the top
                of this page. Your continued
                use of the Service after changes take effect constitutes acceptance of the new Terms.
              </p>

              <h2>18. General Provisions</h2>
              <p>
                <strong>Your Statutory Rights:</strong> Nothing in these Terms excludes, restricts,
                or modifies any right or remedy you have under the law of the place where you live
                that cannot lawfully be excluded, restricted, or modified by agreement. Where any
                provision of these Terms conflicts with such a right, the right prevails and the
                provision applies only to the maximum extent permitted by law.
              </p>
              <p>
                <strong>Governing Law:</strong> These Terms are governed by the laws of the State of
                California, United States, without regard to conflict of law principles — except that
                if you are a consumer, this choice of law does not deprive you of the protection of
                mandatory provisions of the law of the country where you live.
              </p>
              <p>
                <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy,
                constitute the entire agreement between you and CueSesh regarding the Service.
              </p>
              <p>
                <strong>Severability:</strong> If any provision of these Terms is found unenforceable,
                the remaining provisions will continue in full force and effect.
              </p>
              <p>
                <strong>Waiver:</strong> Our failure to enforce any right or provision of these Terms
                will not be considered a waiver of those rights.
              </p>
              <p>
                <strong>Assignment:</strong> You may not assign or transfer these Terms without our
                prior written consent. We may assign our rights and obligations without restriction.
              </p>

              <h2>19. Contact</h2>
              <p>
                Questions about these Terms? Contact us:
              </p>
              <a href="mailto:support@cuesesh.com" className="contact-email-link">
                support@cuesesh.com
              </a>
              <p style={{ marginTop: '1rem' }}>
                CueSesh LLC<br />
                7216 Bergenline Ave #1054<br />
                North Bergen, NJ 07047, United States<br />
                Phone: (917) 830-5632
              </p>

              <div className="legal-highlight" style={{ marginTop: '2rem' }}>
                <p style={{ margin: 0 }}>
                  <strong>Thank you for using CueSesh!</strong><br />
                  We're building something special for podcast lovers, and we're glad you're here.
                </p>
              </div>
            </div>
          </div>
        </div>

        <footer className="legal-footer">
          <div className="legal-footer-links">
            <a href="/privacy" className="legal-footer-link">Privacy</a>
            <a href="/terms" className="legal-footer-link">Terms</a>
            <a href="/contact" className="legal-footer-link">Contact</a>
          </div>
          <p className="legal-footer-text">
            © 2026 CueSesh. All rights reserved.
          </p>
        </footer>
      </div>
    </>
  );
}
